import { useLanguage } from "@/context/language"
import { ButtonV2 } from "@opencode-ai/ui/v2/button-v2"
import { Dialog, DialogBody, DialogFooter, DialogHeader, DialogTitle } from "@opencode-ai/ui/v2/dialog-v2"
import { DividerV2 } from "@opencode-ai/ui/v2/divider-v2"
import { Field } from "@opencode-ai/ui/v2/field-v2"
import { TextInputV2 } from "@opencode-ai/ui/v2/text-input-v2"
import { useDialog } from "@opencode-ai/ui/context/dialog"
import { createStore } from "solid-js/store"
import { Show } from "solid-js"

const PERIOD_CATEGORIES = ["quarterly", "half_yearly", "annual", "monthly", "weekly", "daily"] as const
const PERIOD_OPTIONS: Record<(typeof PERIOD_CATEGORIES)[number], string[]> = {
  quarterly: ["Q1", "Q2", "Q3", "Q4"],
  half_yearly: ["H1", "H2"],
  annual: ["Y1"],
  monthly: ["M01", "M02", "M03", "M04", "M05", "M06", "M07", "M08", "M09", "M10", "M11", "M12"],
  weekly: [],
  daily: [],
}

export function DialogDatoxProjectForm(props: {
  title: string
  initialName?: string
  submitLabel: string
  onSubmit: (name: string) => void
}) {
  const language = useLanguage()
  const dialog = useDialog()
  const [store, setStore] = createStore({ name: props.initialName ?? "" })
  const submit = (event: SubmitEvent) => {
    event.preventDefault()
    const name = store.name.trim()
    if (!name) return
    dialog.close()
    props.onSubmit(name)
  }

  return (
    <Dialog fit>
      <form class="contents" onSubmit={submit}>
        <DialogHeader>
          <DialogTitle>{props.title}</DialogTitle>
        </DialogHeader>
        <DividerV2 />
        <DialogBody class="flex w-96 flex-col gap-4 px-4 pt-4 pb-1">
          <Field>
            <Field.Label>{language.t("datox.project.name")}</Field.Label>
            <TextInputV2
              autofocus
              appearance="large"
              class="!w-full"
              value={store.name}
              onInput={(event) => setStore("name", event.currentTarget.value)}
            />
          </Field>
        </DialogBody>
        <DialogFooter>
          <ButtonV2 type="button" variant="neutral" onClick={() => dialog.close()}>
            {language.t("common.cancel")}
          </ButtonV2>
          <ButtonV2 type="submit" variant="contrast" disabled={!store.name.trim()}>
            {props.submitLabel}
          </ButtonV2>
        </DialogFooter>
      </form>
    </Dialog>
  )
}

export function DialogDatoxDeleteProject(props: { name: string; onDelete: () => void }) {
  const language = useLanguage()
  const dialog = useDialog()
  const remove = () => {
    dialog.close()
    props.onDelete()
  }

  return (
    <Dialog fit>
      <DialogHeader>
        <DialogTitle>{language.t("datox.project.deleteTitle")}</DialogTitle>
      </DialogHeader>
      <DividerV2 />
      <DialogBody class="flex w-96 flex-col gap-2 px-4 pt-4 pb-1">
        <p class="text-14-regular text-v2-text-text-base">
          {language.t("datox.project.deleteConfirm", { name: props.name })}
        </p>
        <p class="text-12-regular text-v2-text-text-muted">{language.t("datox.project.deleteDescription")}</p>
      </DialogBody>
      <DialogFooter>
        <ButtonV2 type="button" variant="neutral" onClick={() => dialog.close()}>
          {language.t("common.cancel")}
        </ButtonV2>
        <ButtonV2 type="button" variant="danger" onClick={remove}>
          {language.t("common.delete")}
        </ButtonV2>
      </DialogFooter>
    </Dialog>
  )
}

export function DialogDatoxDeleteSession(props: { name: string; onDelete: () => void }) {
  const language = useLanguage()
  const dialog = useDialog()
  const remove = () => {
    dialog.close()
    props.onDelete()
  }

  return (
    <Dialog fit>
      <DialogHeader>
        <DialogTitle>{language.t("session.delete.title")}</DialogTitle>
      </DialogHeader>
      <DividerV2 />
      <DialogBody class="flex w-96 flex-col gap-2 px-4 pt-4 pb-1">
        <p class="text-14-regular text-v2-text-text-base">
          {language.t("session.delete.confirm", { name: props.name })}
        </p>
      </DialogBody>
      <DialogFooter>
        <ButtonV2 type="button" variant="neutral" onClick={() => dialog.close()}>
          {language.t("common.cancel")}
        </ButtonV2>
        <ButtonV2 type="button" variant="danger" onClick={remove}>
          {language.t("session.delete.button")}
        </ButtonV2>
      </DialogFooter>
    </Dialog>
  )
}

export function DialogDatoxLogout(props: { user: string; onLogout: () => void }) {
  const language = useLanguage()
  const dialog = useDialog()
  const logout = () => {
    dialog.close()
    props.onLogout()
  }

  return (
    <Dialog fit>
      <DialogHeader>
        <DialogTitle>{language.t("datox.profile.logoutTitle")}</DialogTitle>
      </DialogHeader>
      <DividerV2 />
      <DialogBody class="flex w-96 flex-col gap-2 px-4 pt-4 pb-1">
        <p class="text-14-regular text-v2-text-text-base">
          {language.t("datox.profile.logoutConfirm", { user: props.user })}
        </p>
        <p class="text-12-regular text-v2-text-text-muted">{language.t("datox.profile.logoutDescription")}</p>
      </DialogBody>
      <DialogFooter>
        <ButtonV2 type="button" variant="neutral" onClick={() => dialog.close()}>
          {language.t("common.cancel")}
        </ButtonV2>
        <ButtonV2 type="button" variant="danger" onClick={logout}>
          {language.t("datox.profile.logout")}
        </ButtonV2>
      </DialogFooter>
    </Dialog>
  )
}

export function DialogDatoxPeriodForm(props: {
  onSubmit: (value: { name: string; metadata: Record<string, string | number> }) => void
}) {
  const language = useLanguage()
  const dialog = useDialog()
  const years = periodYears()
  const [store, setStore] = createStore({
    category: "quarterly" as (typeof PERIOD_CATEGORIES)[number],
    period: "Q1",
    year: String(new Date().getFullYear()),
    date: new Date().toISOString().slice(0, 10),
    week: `${new Date().getFullYear()}-W01`,
  })

  const setCategory = (category: (typeof PERIOD_CATEGORIES)[number]) => {
    setStore("category", category)
    setStore("period", PERIOD_OPTIONS[category][0] ?? "")
  }

  const submit = (event: SubmitEvent) => {
    event.preventDefault()
    const value = periodValue(store.category, store.period, store.year, store.date, store.week)
    dialog.close()
    props.onSubmit({
      name: value.label,
      metadata: {
        period_category: value.category,
        period: value.period,
        year: value.year,
      },
    })
  }

  return (
    <Dialog fit>
      <form class="contents" onSubmit={submit}>
        <DialogHeader>
          <DialogTitle>{language.t("datox.period.createTitle")}</DialogTitle>
        </DialogHeader>
        <DividerV2 />
        <DialogBody class="flex w-96 flex-col gap-4 px-4 pt-4 pb-1">
          <Field>
            <Field.Label>{language.t("datox.period.category")}</Field.Label>
            <select
              class="h-9 rounded-md border border-v2-border-border-base bg-v2-background-bg-layer-01 px-2 text-v2-text-text-base outline-none"
              value={store.category}
              onChange={(event) => setCategory(event.currentTarget.value as (typeof PERIOD_CATEGORIES)[number])}
            >
              {PERIOD_CATEGORIES.map((category) => (
                <option value={category}>{periodCategoryLabel(language, category)}</option>
              ))}
            </select>
          </Field>

          <Show when={store.category !== "daily" && store.category !== "weekly"}>
            <Field>
              <Field.Label>{language.t("datox.period.period")}</Field.Label>
              <select
                class="h-9 rounded-md border border-v2-border-border-base bg-v2-background-bg-layer-01 px-2 text-v2-text-text-base outline-none"
                value={store.period}
                onChange={(event) => setStore("period", event.currentTarget.value)}
              >
                {PERIOD_OPTIONS[store.category].map((period) => (
                  <option value={period}>{period}</option>
                ))}
              </select>
            </Field>
            <Field>
              <Field.Label>{language.t("datox.period.year")}</Field.Label>
              <select
                class="h-9 rounded-md border border-v2-border-border-base bg-v2-background-bg-layer-01 px-2 text-v2-text-text-base outline-none"
                value={store.year}
                onChange={(event) => setStore("year", event.currentTarget.value)}
              >
                {years.map((year) => (
                  <option value={year}>{year}</option>
                ))}
              </select>
            </Field>
          </Show>

          <Show when={store.category === "daily"}>
            <Field>
              <Field.Label>{language.t("datox.period.date")}</Field.Label>
              <input
                class="h-9 rounded-md border border-v2-border-border-base bg-v2-background-bg-layer-01 px-2 text-v2-text-text-base outline-none"
                type="date"
                value={store.date}
                onInput={(event) => setStore("date", event.currentTarget.value)}
              />
            </Field>
          </Show>

          <Show when={store.category === "weekly"}>
            <Field>
              <Field.Label>{language.t("datox.period.week")}</Field.Label>
              <input
                class="h-9 rounded-md border border-v2-border-border-base bg-v2-background-bg-layer-01 px-2 text-v2-text-text-base outline-none"
                type="week"
                value={store.week}
                onInput={(event) => setStore("week", event.currentTarget.value)}
              />
            </Field>
          </Show>
        </DialogBody>
        <DialogFooter>
          <ButtonV2 type="button" variant="neutral" onClick={() => dialog.close()}>
            {language.t("common.cancel")}
          </ButtonV2>
          <ButtonV2 type="submit" variant="contrast">
            {language.t("datox.period.create")}
          </ButtonV2>
        </DialogFooter>
      </form>
    </Dialog>
  )
}

function periodYears() {
  const current = new Date().getFullYear() + 1
  return Array.from({ length: 6 }, (_, index) => String(current - index))
}

function periodCategoryLabel(language: ReturnType<typeof useLanguage>, category: string) {
  switch (category) {
    case "daily":
      return language.t("datox.period.category.daily")
    case "weekly":
      return language.t("datox.period.category.weekly")
    case "monthly":
      return language.t("datox.period.category.monthly")
    case "quarterly":
      return language.t("datox.period.category.quarterly")
    case "half_yearly":
      return language.t("datox.period.category.half_yearly")
    case "annual":
      return language.t("datox.period.category.annual")
    default:
      return category
  }
}

function periodValue(category: string, period: string, year: string, date: string, week: string) {
  if (category === "daily") {
    const [yyyy, mm, dd] = date.split("-")
    const normalized = `${dd}-${mm}-${yyyy}`
    return { category, period: normalized, year: Number(yyyy), label: normalized }
  }
  if (category === "weekly") {
    const [yyyy, rawWeek] = week.split("-W")
    const normalized = `W${rawWeek}`
    return { category, period: normalized, year: Number(yyyy), label: `${normalized}, ${yyyy}` }
  }
  return { category, period, year: Number(year), label: `${period}, ${year}` }
}
